import { defineStore } from 'pinia'
import { testStatus } from '@/types/test-status'
import { Language } from '@/types/language'
import { Promo } from '@/types/promo'
import { Question } from '@/types/question'
import { Answer } from '@/types/answer'
import axios from 'axios'
import { Skintest, Urls } from '@/types/api'
import { IProduct } from '@/components/ProductSlider/ProductSlider'
import { ICartItem, ICart } from '@/components/Cart/Cart'
import { Result } from '@/types/result'
import { Images } from '@/types/images'
import { Config } from '@/types/config'

export const useMainStore = defineStore('main', {
  state: () => ({
    answer: <Array<Answer['uuid']>>[],
    answers: {},
    testStatus: testStatus.notStarted,
    page: 1,
    pages: 1,
    questions: <Question[]>[],
    snippets: <Language>{},
    promoPopup: <Promo>{},
    cart: <ICart>{},
    cartOpen: false,
    result: <Result>{},
    images: <Images>{},
    urls: <Urls>{},
    resultUuid: <Result['resultUuid'] | null>null,
    name: '',
    checkoutUrl: '',
    checkoutUrlLoading: false,
    userConfirmation: false,
    referrer: '',
  }),
  getters: {
    question(): Question {
      return this.questions[this.page - 1]
    },
    nextPage(): number {
      const nextPage = this.page + 1
      return nextPage <= this.pages ? nextPage : this.pages
    },
    prevPage(): number {
      const prevPage = this.page - 1
      return prevPage >= 0 ? prevPage : 0
    },
    getAnswers(): Array<Answer['uuid']> {
      const questionId = this.question?.uuid
      return Reflect.get(this.answers, questionId)
    },
    getLastAnsweredQuestion(): number {
      return Object.keys(this.answers).length
    },
    cartCounter(): number {
      let total = 0
      for (const [key, value] of Object.entries(this.cart)) {
        total += this.cart[key].qty
      }

      return total
    },
  },
  // optional actions
  actions: {
    async init(config: Config): Promise<void> {
      let response
      try {
        // request Skintest
        response = await axios.get(`${config.baseUrl}/api/v1/questionnaire/${config.testSlug}`)
      } catch (error) {
        console.error(error)
      }
      // type cast response to response type of skintest
      const apiResponse = response?.data as Skintest

      // set global config

      config.features = apiResponse.features

      // set Questions
      this.setQuestions(apiResponse.questions)
      // set pages
      this.setPages(apiResponse.questions.length)
      // set promo popup
      this.setPromoPopup(apiResponse.promo)
      // set language
      this.setSnippets(apiResponse.language)
      // set Urls
      this.setUrls(apiResponse.urls)
      // set Image from repsonse
      this.setImage('result_image', apiResponse.result_image)
      this.setImage('day_care_image', apiResponse.day_care_image)
      this.setImage('night_care_image', apiResponse.night_care_image)
      this.setImage('med_care_image_1', apiResponse.med_care_image_1)
      this.setImage('med_care_image_2', apiResponse.med_care_image_2)
      // set name
      this.setName(apiResponse.name)
    },
    saveAnswer(): void {
      const questionId = this.question.uuid
      Reflect.set(this.answers, questionId, this.answer)
    },
    setAnswer(answer: Array<Answer['uuid']>): void {
      this.answer = answer
    },
    resetAnswers(): void {
      this.answers = {}
      this.answer = []
    },
    resetCart(): void {
      this.cart = {}
    },
    setStatus(status: testStatus): void {
      this.testStatus = status
    },
    setPage(page: number): void {
      this.page = page
    },
    setPages(pages: number): void {
      this.pages = pages
    },
    setPromoPopup(promoPopup: Promo): void {
      this.promoPopup = promoPopup
    },
    setQuestions(questions: Question[]): void {
      this.questions = questions
    },
    reset(): void {
      this.page = 1
    },
    setSnippets(snippets: Language): void {
      this.snippets = snippets
    },
    setUrls(urls: Urls): void {
      this.urls = urls
    },
    setUserconfirmation(userConfirmation: boolean): void {
      this.userConfirmation = userConfirmation
    },
    addToCart(product: IProduct | ICartItem): void {
      const currentProduct = this.cart[product.id]
      const productQty: number = currentProduct?.qty > 0 ? currentProduct.qty + product.qty : product.qty

      const cartItem: ICartItem = {
        id: product.id,
        title: product.title,
        price: product.price * productQty,
        productImage: product.productImage,
        currency: product.currency,
        qty: productQty,
      }

      Reflect.set(this.cart, product.id, cartItem)
    },
    async updateBackendCart(config: any): Promise<void> {
      let response
      const listData = this.cart

      let cartItems: any = []
      for (const [key, value] of Object.entries(listData)) {
        cartItems.push([listData[key].id, listData[key].qty])
      }

      try {
        this.checkoutUrlLoading = true
        response = await axios.post(`${config.baseUrl}/api/v1/questionnaire/${config.testSlug}/finish`, {
          basket: Object.fromEntries(cartItems),
          approval: this.userConfirmation,
          resultUuid: this.resultUuid,
        })
      } catch (error) {
        console.error(error)
      }
      if (response) {
        this.checkoutUrlLoading = false
        this.checkoutUrl = response.data
      }
    },
    removeFromCart(id: number): void {
      if (this.cart[id]) delete this.cart[id]
    },
    openCart(): void {
      this.cartOpen = true
    },
    closeCart(): void {
      this.cartOpen = false
    },
    setResult(result: Result): void {
      this.result = result
      this.resultUuid = result.resultUuid
    },
    setResultUuid(uuid: Result['resultUuid']): void {
      this.resultUuid = uuid
    },
    resetResult(): void {
      this.result = <Result>{}
    },
    setImage(key: string, value: string) {
      Reflect.set(this.images, key, value)
    },
    setName(name: string): void {
      this.name = name
    },
    setReferrer(referrer: string): void {
      this.referrer = referrer
    },
  },
})
